import { useCallback, useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Boxes, Plus, Pencil, Trash2, ChevronRight, Loader2, Calculator } from "lucide-react";

const EMPTY = { omschrijving: "", aanschafwaarde: "", aanschafdatum: new Date().toISOString().slice(0, 10), categorie: "", termijn_jaren: "", restwaarde: "0" };
const nlDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };

export default function VasteActiva() {
  const [assets, setAssets] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get("/vaste-activa"), api.get("/vaste-activa/categories")]);
      setAssets(a.data.assets); setCats(c.data.categories);
    } catch { toast.error("Laden mislukt"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (a) => {
    setForm({ omschrijving: a.omschrijving, aanschafwaarde: String(a.aanschafwaarde),
      aanschafdatum: a.aanschafdatum, categorie: a.categorie,
      termijn_jaren: String(a.termijn_jaren), restwaarde: String(a.restwaarde || 0) });
    setEditId(a.id); setOpen(true);
  };
  const onCat = (key) => {
    const c = cats.find((x) => x.key === key);
    setForm((f) => ({ ...f, categorie: key, termijn_jaren: f.termijn_jaren || (c ? String(c.term) : "") }));
  };
  const save = async () => {
    if (!form.omschrijving || !form.categorie || !form.aanschafwaarde || !form.termijn_jaren) {
      toast.error("Vul omschrijving, categorie, aanschafwaarde en termijn in"); return;
    }
    setSaving(true);
    const payload = { omschrijving: form.omschrijving, aanschafwaarde: parseFloat(form.aanschafwaarde),
      aanschafdatum: form.aanschafdatum, categorie: form.categorie,
      termijn_jaren: parseInt(form.termijn_jaren, 10), restwaarde: parseFloat(form.restwaarde || 0) };
    try {
      if (editId) await api.patch(`/vaste-activa/${editId}`, payload);
      else await api.post("/vaste-activa", payload);
      toast.success(editId ? "Vast actief bijgewerkt" : "Vast actief toegevoegd");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };
  const del = async (a) => {
    if (!window.confirm(`"${a.omschrijving}" verwijderen? De afschrijvingsboekingen verdwijnen uit het grootboek.`)) return;
    try { await api.delete(`/vaste-activa/${a.id}`); toast.success("Verwijderd"); load(); }
    catch { toast.error("Verwijderen mislukt"); }
  };

  const totalAanschaf = assets.reduce((s, a) => s + (a.aanschafwaarde || 0), 0);
  const totalBoekwaarde = assets.reduce((s, a) => s + (a.boekwaarde || 0), 0);

  return (
    <div className="space-y-6" data-testid="vaste-activa-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Boxes className="h-6 w-6 text-violet-600" /> Vaste activa
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Lineaire afschrijving, pro rata temporis. Afschrijvingen worden automatisch geboekt (630200 aan geboekte afschrijvingen) en verschijnen in het grootboek en de resultatenrekening.</p>
        </div>
        <Button className="bg-violet-600 hover:bg-violet-700" data-testid="add-asset-btn" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1.5" /> Vast actief toevoegen
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="rounded-xl border p-4 bg-card"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Aantal activa</div><div className="text-2xl font-semibold font-heading mt-1">{assets.length}</div></div>
        <div className="rounded-xl border p-4 bg-card"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Aanschafwaarde</div><div className="text-2xl font-semibold font-heading mt-1 tabular-nums">{eur(totalAanschaf)}</div></div>
        <div className="rounded-xl border p-4 bg-violet-50 border-violet-200"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Huidige boekwaarde</div><div className="text-2xl font-semibold font-heading mt-1 tabular-nums">{eur(totalBoekwaarde)}</div></div>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-16">Laden…</div>
        : assets.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">
            <Boxes className="h-10 w-10 mx-auto mb-3 opacity-40" />
            Nog geen vaste activa. Voeg er één toe of vink "bedrijfsmiddel" aan bij een aankoopfactuur.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {assets.map((a) => {
              const isOpen = !!expanded[a.id];
              return (
                <Card key={a.id} data-testid={`asset-${a.id}`}>
                  <CardContent className="p-0">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button onClick={() => setExpanded((e) => ({ ...e, [a.id]: !e[a.id] }))}
                        className="flex items-center gap-2 flex-1 text-left" data-testid={`asset-toggle-${a.id}`}>
                        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        <div>
                          <div className="font-medium">{a.omschrijving}</div>
                          <div className="text-xs text-muted-foreground">{a.categorie_label} · {a.termijn_jaren} jaar · aangekocht {nlDate(a.aanschafdatum)}</div>
                        </div>
                      </button>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">Boekwaarde</div>
                        <div className="font-semibold tabular-nums">{eur(a.boekwaarde)}</div>
                      </div>
                      <div className="text-right hidden sm:block w-28">
                        <div className="text-xs text-muted-foreground">Aanschaf</div>
                        <div className="tabular-nums">{eur(a.aanschafwaarde)}</div>
                      </div>
                      <Button variant="ghost" size="icon" data-testid={`asset-edit-${a.id}`} onClick={() => openEdit(a)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-red-600" data-testid={`asset-del-${a.id}`} onClick={() => del(a)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    {isOpen && (
                      <div className="border-t bg-muted/20 px-4 py-3" data-testid={`asset-schema-${a.id}`}>
                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2"><Calculator className="h-3.5 w-3.5" /> Afschrijvingstabel</div>
                        <div className="grid grid-cols-4 gap-2 text-xs font-medium text-muted-foreground border-b pb-1">
                          <span>Jaar</span><span className="text-right">Boekwaarde begin</span><span className="text-right">Afschrijving</span><span className="text-right">Boekwaarde eind</span>
                        </div>
                        {a.schema.map((r) => (
                          <div key={r.jaar} className="grid grid-cols-4 gap-2 text-sm py-1 border-b border-dashed last:border-0">
                            <span>{r.jaar}</span>
                            <span className="text-right tabular-nums">{eur(r.boekwaarde_begin)}</span>
                            <span className="text-right tabular-nums text-violet-700 font-medium">{eur(r.afschrijving)}</span>
                            <span className="text-right tabular-nums">{eur(r.boekwaarde_eind)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="asset-dialog">
          <DialogHeader><DialogTitle>{editId ? "Vast actief bewerken" : "Vast actief toevoegen"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Omschrijving</Label>
              <Input data-testid="asset-omschrijving" value={form.omschrijving} onChange={(e) => setForm((f) => ({ ...f, omschrijving: e.target.value }))} placeholder="bv. Toonbank koeling" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Aanschafwaarde (excl. btw)</Label>
                <Input type="number" step="0.01" data-testid="asset-waarde" value={form.aanschafwaarde} onChange={(e) => setForm((f) => ({ ...f, aanschafwaarde: e.target.value }))} placeholder="0,00" />
              </div>
              <div className="space-y-1.5">
                <Label>Aanschafdatum</Label>
                <Input type="date" data-testid="asset-datum" value={form.aanschafdatum} onChange={(e) => setForm((f) => ({ ...f, aanschafdatum: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categorie</Label>
                <Select value={form.categorie} onValueChange={onCat}>
                  <SelectTrigger data-testid="asset-categorie"><SelectValue placeholder="Kies…" /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label} ({c.term}j)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Afschrijvingstermijn (jaren)</Label>
                <Input type="number" min="1" data-testid="asset-termijn" value={form.termijn_jaren} onChange={(e) => setForm((f) => ({ ...f, termijn_jaren: e.target.value }))} placeholder="bv. 5" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Restwaarde (optioneel)</Label>
              <Input type="number" step="0.01" data-testid="asset-restwaarde" value={form.restwaarde} onChange={(e) => setForm((f) => ({ ...f, restwaarde: e.target.value }))} placeholder="0,00" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button className="bg-violet-600 hover:bg-violet-700" data-testid="asset-save-btn" disabled={saving} onClick={save}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
